/**
 * Bucketing of stats responses for the dashboard performance chart. Pure.
 */
import { IStatItem } from '@/interfaces/IStatItem';
import { IStatGroup } from '@/interfaces/IStatGroup';
import { businessHourPosition } from '@/domain/stats/business-day';

/**
 * Hourly values along the business-day axis (position 0 = start hour),
 * truncated to the first `count` positions. Values are added, so a clock
 * hour repeated on a DST fall-back day is summed, not overwritten.
 */
export function bucketStatsByBusinessHour(
  stats: IStatItem[],
  cutoffHour: number,
  count: number
): number[] {
  const values: number[] = new Array(24).fill(0);
  for (const stat of stats) {
    if (stat.hour === undefined) continue;
    values[businessHourPosition(stat.hour, cutoffHour)] += stat.value;
  }
  return values.slice(0, count);
}

/**
 * Values summed per yyyyMMdd day. `forcedDayKey` sums every stat under that
 * day: a single-day range the API answered hourly ("Este mes" on the 1st),
 * whose post-midnight buckets carry the next calendar date.
 */
export function bucketStatsByDay(
  stats: IStatItem[],
  forcedDayKey?: string
): Record<string, number> {
  const values: Record<string, number> = {};
  for (const stat of stats) {
    const key = forcedDayKey ?? stat.date;
    if (key === undefined) continue;
    values[key] = (values[key] ?? 0) + stat.value;
  }
  return values;
}

/**
 * All groups added up bucket by bucket (menu views = links + tables +
 * otherQr). Keyed by date AND hour: hourly buckets carry both, so keying by
 * date alone would collapse a whole day into one point.
 */
export function sumStatGroupsByBucket(groups: IStatGroup[]): IStatItem[] {
  const sums = new Map<string, IStatItem>();
  for (const group of groups) {
    for (const stat of group.stats ?? []) {
      const key = `${stat.date ?? ''}|${stat.hour ?? ''}`;
      const current = sums.get(key);
      if (current) {
        current.value += stat.value;
      } else {
        sums.set(key, { ...stat });
      }
    }
  }
  return Array.from(sums.values());
}
